import DOMPurify from 'dompurify';
import { IStrapiResponse, IWidget } from '@/Models';

export interface WithdrawalMethod {
  id: string;
  name: string;
  priority: number;
  icon: string;
  iconDarkTheme: string;
  description: string;
  bodyHtml: string;
  bodyWithoutWrappers: string;
  available: boolean;
  children?: WithdrawalMethodChild[];
}

export interface WithdrawalMethodChild {
  id: string;
  name: string;
  icon: string;
  iconDarkTheme: string;
  description: string;
  bodyHtml: string;
  bodyWithoutWrappers: string;
}

/**
 * Extract withdrawal methods from Strapi BankIcons widget
 */
export const extractWithdrawalMethods = (
  strapiData: IStrapiResponse
): WithdrawalMethod[] => {
  const bankIconsWidget = strapiData.widgets.find(
    (w) => w.name === 'BankIcons'
  );

  if (!bankIconsWidget) return [];

  const bankIconsData = bankIconsWidget.data.find(
    (d) => d.key === 'BankIcons'
  );

  if (!bankIconsData || !Array.isArray(bankIconsData.value)) return [];

  return bankIconsData.value
    .map((item: any) => ({
      id: generateId(item.name),
      name: item.name,
      priority: item.priority,
      icon: item.icon,
      iconDarkTheme: item.iconDarkTheme || item.icon,
      description: htmlToText(item.body || ''),
      bodyHtml: sanitizeHtml(item.body || ''),
      bodyWithoutWrappers: stripWrapperTags(item.body || ''),
      available: true,
      children: item.children?.map((child: any) => ({
        id: generateId(child.name),
        name: child.name,
        icon: child.icon,
        iconDarkTheme: child.iconDarkTheme || child.icon,
        description: htmlToText(child.body || ''),
        bodyHtml: sanitizeHtml(child.body || ''),
        bodyWithoutWrappers: stripWrapperTags(child.body || ''),
      })),
    }))
    .sort((a, b) => a.priority - b.priority);
};

/**
 * Extract turnover threshold text from Strapi
 */
export const extractTurnoverText = (strapiData: IStrapiResponse): string => {
  const turnoverWidget = strapiData.widgets.find(
    (w) => w.name === 'TurnoverThresholdText'
  );

  if (!turnoverWidget) return '';

  const bodyData = turnoverWidget.data.find((d) => d.key === 'Body');

  const value = bodyData?.value;

  return typeof value === 'string' ? value.trim() : '';
};

/**
 * Get withdrawal method by ID
 */
export const getWithdrawalMethodById = (
  strapiData: IStrapiResponse,
  methodId: string
): WithdrawalMethod | undefined => {
  const methods = extractWithdrawalMethods(strapiData);

  return methods.find((method) => method.id === methodId);
};

/**
 * Get widget by name
 */
export const getWidgetByName = (
  strapiData: IStrapiResponse,
  widgetName: string
): IWidget | undefined => {
  return strapiData.widgets.find((w) => w.name === widgetName);
};

/**
 * Generate consistent ID from name
 */
const generateId = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .replace(/\s+/g, '');
};

/**
 * Sanitize HTML safely using DOMPurify
 */
const sanitizeHtml = (html: string): string => {
  if (!html) return '';

  return DOMPurify.sanitize(html);
};

/**
 * Convert HTML to plain text safely
 */
const htmlToText = (html: string): string => {
  if (!html) return '';

  const sanitized = DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });

  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitized, 'text/html');

  return doc.body.textContent?.trim() ?? '';
};

/**
 * Remove wrapper div tags but preserve inner HTML formatting
 */
const stripWrapperTags = (html: string): string => {
  if (!html) return '';

  const sanitized = DOMPurify.sanitize(html);

  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitized, 'text/html');

  const firstElement = doc.body.firstElementChild;

  if (firstElement && firstElement.tagName === 'DIV') {
    return firstElement.innerHTML.trim();
  }

  return doc.body.innerHTML.trim();
};

/**
 * Fix retailers link in HTML content
 * Replaces relative or broken retailers links with proper URL
 */
export const fixRetailersLink = (html: string, premiumSourceUrl: string): string => {
  if (!html) return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Find all anchor tags
  const links = doc.querySelectorAll('a');

  links.forEach((link) => {
    const text = link.textContent?.toLowerCase() || '';

    // Check if this is the retailers link
    if (text.includes('retailer')) {
      // Set the correct URL - same as Instant Money
      const correctUrl = `${premiumSourceUrl}/content/info-help?contentName=cash-send-terms-and-conditions`;
      link.setAttribute('href', correctUrl);
      // Open in new tab like the working BankDetails implementation
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
      link.style.textDecoration = 'underline';
    }
  });

  return doc.body.innerHTML;
};